import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Divider,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../Snackbar/SnackbarContext";

const STATUS_OPTIONS = ["Completed", "In Progress", "Needs Support", "Absent"];

const ActivityProgressForm = ({ activity, class_guid, onSubmit, onCancel }) => {
  const [students, setStudents] = useState([]);
  const [progress, setProgress] = useState({});
  const { showSnackbar } = useSnackbar();

  /* ===============================
      1️⃣ FETCH STUDENTS
  =============================== */
  const fetchStudents = async () => {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${apiEndpoints.activityStudents}?branch_guid=${activity.branch_guid}&class_guid=${class_guid}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const json = await res.json();
    if (json.status === "success") {
      let filtered = json.data || [];
      if (
        Array.isArray(activity.student_guids) &&
        activity.student_guids.length > 0
      ) {
        filtered = filtered.filter((s) =>
          activity.student_guids.includes(s.guid)
        );
      }
      setStudents(filtered);
    }
  };

  /* ===============================
      2️⃣ FETCH PROGRESS
  =============================== */
  const fetchProgress = async () => {
  const token = localStorage.getItem("token");

  const res = await fetch(
    `${apiEndpoints.activityProgress}?activity_guid=${activity.guid}&branch_guid=${activity.branch_guid}&class_guid=${class_guid}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  const text = await res.text();

  if (!text || text.startsWith("<")) {
    console.error("Invalid progress response:", text);
    return;
  }

  const json = JSON.parse(text);

  if (json.status === "success") {
    const map = {};
    json.data.forEach((p) => {
      map[p.student_guid] = {
        rating: Number(p.rating) || 0,
        status: p.status || "",
        notes: p.remarks || "",
      };
    });
    setProgress(map);
  }
};

  /* ===============================
      LOAD ON OPEN
  =============================== */
  useEffect(() => {
    if (!activity?.guid || !class_guid) return;
    fetchStudents();
    fetchProgress();
  }, [activity.guid, class_guid]);

  /* ===============================
      MERGE STUDENTS + PROGRESS
  =============================== */
  useEffect(() => {
    if (!students.length) return;

    setProgress((prev) => {
      const merged = { ...prev };
      students.forEach((s) => {
        if (!merged[s.guid]) {
          merged[s.guid] = {
            rating: 0,
            status: "",
            notes: "",
          };
        }
      });
      return merged;
    });
  }, [students]);

  /* ===============================
      UPDATE FIELD
  =============================== */
  const updateProgress = (student_guid, field, value) => {
    setProgress((old) => ({
      ...old,
      [student_guid]: {
        ...old[student_guid],
        [field]: value,
      },
    }));
  };

  /* ===============================
      SUBMIT
  =============================== */
  const handleSubmit = () => {
    const payload = {
      activity_guid: activity.guid,
        branch_guid: activity.branch_guid, // ✅ ADD THIS
      class_guid,
      items: Object.entries(progress).map(([student_guid, values]) => ({
        student_guid,
        ...values,
      })),
    };

    onSubmit?.(payload);
    showSnackbar("Progress saved successfully", "success");
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        ⭐ Submit Progress – {activity.title}
      </Typography>

      <Typography sx={{ mb: 2, color: "text.secondary" }}>
        Mark progress for class <b>{activity.class_name}</b>
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {students.map((s) => (
          <Grid key={s.guid} item xs={12}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #ddd",
              background: "rgba(255,255,255,0.5)",
            }}
          >
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              {s.first_name} {s.last_name}
            </Typography>

            {/* ⭐ Rating */}
            <Box sx={{ display: "flex", mb: 2 }}>
              {[1,2,3,4,5].map((star) => (
                <IconButton
                  key={star}
                  onClick={() => updateProgress(s.guid, "rating", star)}
                >
                  <StarIcon
                    sx={{
                      color:
                        progress[s.guid]?.rating >= star ? "#FFB300" : "#ccc",
                    }}
                  />
                </IconButton>
              ))}
            </Box>

            {/* Status */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {STATUS_OPTIONS.map((status) => (
                <Chip
                  key={status}
                  label={status}
                  clickable
                  onClick={() => updateProgress(s.guid, "status", status)}
                  sx={{
                    bgcolor:
                      progress[s.guid]?.status === status
                        ? "#4ECDC4"
                        : "rgba(0,0,0,0.08)",
                    color:
                      progress[s.guid]?.status === status ? "#fff" : "#000",
                  }}
                />
              ))}
            </Box>

            {/* Notes */}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Progress Notes"
              value={progress[s.guid]?.notes || ""}
              onChange={(e) =>
                updateProgress(s.guid, "notes", e.target.value)
              }
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "end", gap: 2 }}>
        <Button variant="outlined" onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Submit Progress
        </Button>
      </Box>
    </Box>
  );
};

export default ActivityProgressForm;
